/**
 * Sysadmin Studio Prompts hooks (Wave 3 b73) — wraps
 * /admin/platform/studio-prompts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

export interface SysadminPromptVariable {
  name: string;
  label: string;
  type?: string;
  required?: boolean;
  default?: string;
  options?: string[];
}

export interface SysadminPrompt {
  id: string;
  organizationId: string;
  createdBy: string;
  command: string;
  title: string;
  description: string | null;
  content: string;
  variables: SysadminPromptVariable[];
  category: string | null;
  isGlobal: boolean | null;
  visibleToTenant: boolean;
  sortOrder: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface SysadminPromptListResponse {
  items: SysadminPrompt[];
  total: number;
}

const KEYS = {
  list: ['sysadmin-studio-prompts'] as const,
};

export function useSysadminPrompts(opts: { visibleOnly?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.visibleOnly) params.set('visibleOnly', 'true');
  const qs = params.toString();

  return useQuery<SysadminPromptListResponse, Error>({
    queryKey: [...KEYS.list, opts],
    queryFn: () =>
      apiClient.get<SysadminPromptListResponse>(
        `/admin/platform/studio-prompts${qs ? `?${qs}` : ''}`,
      ),
  });
}

export interface CreateSysadminPromptInput {
  command: string;
  title: string;
  description?: string;
  content: string;
  variables?: SysadminPromptVariable[];
  category?: string;
  visibleToTenant?: boolean;
  sortOrder?: number;
}

export function useCreateSysadminPrompt() {
  const qc = useQueryClient();
  return useMutation<{ data: SysadminPrompt }, Error, CreateSysadminPromptInput>({
    mutationFn: (input) =>
      apiClient.post<{ data: SysadminPrompt }>(
        '/admin/platform/studio-prompts',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list }),
  });
}

export interface UpdateSysadminPromptInput
  extends Partial<Omit<CreateSysadminPromptInput, 'command'>> {
  id: string;
}

export function useUpdateSysadminPrompt() {
  const qc = useQueryClient();
  return useMutation<{ data: SysadminPrompt }, Error, UpdateSysadminPromptInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.patch<{ data: SysadminPrompt }>(
        `/admin/platform/studio-prompts/${id}`,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list }),
  });
}

export function useToggleSysadminPromptVisibility() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; visibleToTenant: boolean },
    Error,
    { id: string; visibleToTenant: boolean }
  >({
    mutationFn: ({ id, visibleToTenant }) =>
      apiClient.patch(
        `/admin/platform/studio-prompts/${id}/visibility`,
        { visibleToTenant },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list }),
  });
}

export function useDeleteSysadminPrompt() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete(`/admin/platform/studio-prompts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list }),
  });
}
